import { Controller } from "@hotwired/stimulus";

const FLUSH_EVENT = "customization-draft:flush";
const FLUSHED_EVENT = "customization-draft:flushed";
const RESUME_EVENT = "customization-draft:resume";

// Before publish/discard submits, ask the settings form to flush any
// pending autosave so the server-side draft state is settled before we
// promote or wipe it.
export default class extends Controller {
  connect() {
    this.flushed = false;
    this.submitEndHandler = this.handleSubmitEnd.bind(this);
    this.element.addEventListener("turbo:submit-end", this.submitEndHandler);
  }

  disconnect() {
    this.element.removeEventListener("turbo:submit-end", this.submitEndHandler);
  }

  // Reset so a failed submit allows the next click to flush; RESUME_EVENT re-arms the draft controller's autosave.
  handleSubmitEnd() {
    this.flushed = false;
    document.dispatchEvent(new CustomEvent(RESUME_EVENT));
  }

  async flushBeforeSubmit(event) {
    if (this.flushed) return;
    event.preventDefault();
    const form = event.target;
    // Capture the submitter so the resubmit carries it through to Turbo,
    // which uses it to find data-turbo-submits-with for the loading state.
    // requestSubmit() with no argument loses the submitter and so no
    // submits-with text appears on the button.
    const submitter = event.submitter;

    // Wait for any in-flight autosave to settle. Time out after 2s in
    // case the settings form (and its FLUSH_EVENT listener) hasn't
    // connected yet — better to submit with a slightly stale draft than
    // hang forever on a missing handler.
    await new Promise((resolve) => {
      const safety = setTimeout(resolve, 2000);
      document.addEventListener(
        FLUSHED_EVENT,
        () => {
          clearTimeout(safety);
          resolve();
        },
        { once: true },
      );
      document.dispatchEvent(new CustomEvent(FLUSH_EVENT));
    });

    this.flushed = true;
    // Defer to a fresh macrotask. requestSubmit() is a no-op while the
    // form's "firing submission events" flag is true — and that flag
    // stays set through the entire microtask checkpoint following the
    // original submit dispatch. setTimeout pushes us past that, into the
    // next task, where the flag has been cleared and the resubmit fires.
    setTimeout(() => form.requestSubmit(submitter), 0);
  }
}
